/*
 * Copyright (c) 2022, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */
import {ReactPlugin} from '@microsoft/applicationinsights-react-js';
import {ApplicationInsights} from '@microsoft/applicationinsights-web';

const {azureInsightsKey} = Settings;

import {AsgardeoSPAClient} from '@asgardeo/auth-react';
import Settings from "../conf/settings";

const reactPlugin = new ReactPlugin();
const ai = new ApplicationInsights({
    config: {
        instrumentationKey: azureInsightsKey,
        extensions: [reactPlugin],
    },
});
if (azureInsightsKey) {
    ai.loadAppInsights();
}
export const {appInsights} = ai;

export const trackEvent = async (name: string, customProperties?: Record<string, string>) => {
    /* Temporary removed the App insight tracking function due to a GDPR security concerns */
    /*
    if (azureInsightsKey) {
        const properties: { [key: string]: string } = {
            ...customProperties,
        };

        const auth = AsgardeoSPAClient.getInstance();
        if (auth) {
            try {
                const idToken = await auth.getDecodedIDToken();

                if (idToken?.sub) {
                    properties.idpId = idToken.sub;
                }

                const userEmail = idToken?.email;
                if (userEmail) {
                    const isWSO2User = !!userEmail.endsWith('@wso2.com');
                    properties.isWSO2User = `${isWSO2User}`;
                }

            } catch (error) {
                console.error('Failed to get auth details for azure event', error);
            }
        }

        appInsights.trackEvent({name}, properties);
    }
    */
};

export const trackIdpAuthInitiation = (correlationId: string): void => {
    trackEvent("dev-portal-idp-auth-initiation", {devportal_corId: correlationId});
};

export const trackIdpAuthSuccess = (correlationId: string): void => {
    trackEvent("dev-portal-idp-auth-success", {devportal_corId: correlationId});
};

export const trackIdpAuthFailure = (correlationId: string, error: string): void => {
    trackEvent("dev-portal-idp-auth-failure", {devportal_corId: correlationId, error: error});
};

export const trackObtainApimTokenInitiation = (correlationId: string): void => {
    trackEvent("dev-portal-obtain-apim-token-initiation", {devportal_corId: correlationId});
};

export const trackObtainApimTokenSuccess = (correlationId: string): void => {
    trackEvent("dev-portal-obtain-apim-token-success", {devportal_corId: correlationId});
};

export const trackObtainApimTokenFailure = (correlationId: string, error: string): void => {
    trackEvent("dev-portal-obtain-apim-token-failure", {devportal_corId: correlationId, error: error});
};

export const trackFetchUserOrgSuccess = (correlationId: string): void => {
    trackEvent("dev-portal-fetch-user-org-success", {devportal_corId: correlationId});
};

export const trackFetchUserOrgFailure = (correlationId: string, error: string): void => {
    trackEvent("dev-portal-fetch-user-org-failure", {devportal_corId: correlationId, error: error});
};

export const trackLoginPageLoad = (correlationId: string): void => {
    trackEvent('dev-portal-login-page-load', {devportal_corId: correlationId});
};

export const trackSignInTryAgain = (correlationId: string): void => {
    trackEvent('dev-portal-click-try-again-signing', {devportal_corId: correlationId});
};

export const trackHomePageLoad = (correlationId: string): void => {
    trackEvent('dev-portal-page-load', {devportal_corId: correlationId});
};

export const trackOverviewPageLoad = (apiName: string, apiId: string, correlationId: string): void => {
    trackEvent('dev-portal-api-load-overview', {apiName: apiName, apiId: apiId, devportal_corId: correlationId});
};

export const trackCredentialGenerate = (apiName: string, apiId: string, grantTypes: string[]): void => {
    trackEvent('dev-portal-api-generate-credentials', {
        apiName,
        apiId,
        grantTypes: grantTypes.join(",")
    });
};

export const trackTryAPIPageLoad = (): void => {
    trackEvent('dev-portal-api-load-try-out');
};

export const trackBrowseAPIButtonCLick = (): void => {
    trackEvent('dev-portal-browse-apis');
};

export const trackApiButtonClickOnHeader = (): void => {
    trackEvent('dev-portal-navbar-apis');
}

// API Search Page
export const trackApiLoadOnSearch = (query: string, tagList: string[]): void => {
    trackEvent('dev-portal-load-apis', {
        searchQuery: query,
        tagList: tagList.join(",")
    });
};

export const trackApiSelectFromAPIList = (apiId: string): void => {
    trackEvent('dev-portal-select-api', {
        apiId
    });
};

export const trackTryOutClickFromAPICard = (apiId: string): void => {
    trackEvent('dev-portal-select-api-tryout', {
        apiId
    });
};

export const trackDocumentationClickFromAPICard = (apiId: string): void => {
    trackEvent('dev-portal-select-api-documentation', {apiId});
};


// API Overview
export const trackOverviewAddComment = (apiId: string, comment: string): void => {
    trackEvent('dev-portal-api-overview-add-comment', {
        apiId,
        comment
    });
};

export const trackAPIRatingChange = (apiId: string, rate: string): void => {
    trackEvent('dev-portal-api-change-rating', {
        apiId,
        rate
    });
};

// API try Out
export const trackTryOutGoToCredentialsClick = (apiId: string): void => {
    trackEvent('dev-portal-try-out-api-generate-token', {
        apiId
    });
};

export const trackTryOutSwaggerDownload = (apiId: string): void => {
    trackEvent('dev-portal-api-try-out-download-swagger', {
        apiId
    });
};

// API Documentation
export const trackAPIDocumentationNavBarClick = (): void => {
    trackEvent('dev-portal-sidebar-api-documentation');
};

export const trackDocExpandClicks = (apiId: string, docType: string): void => {
    trackEvent('dev-portal-api-documentation-open', {
        apiId,
        docType
    })
}

// API Credentials
export const trackAPICredentialsNavBarClick = (): void => {
    trackEvent('dev-portal-sidebar-api-credentials');
};

export const trackAPICredentialsUpdateClick = (apiId: string): void => {
    trackEvent('dev-portal-api-update-credentials', {apiId});
};

export const trackAPICredentialsRemoveClick = (apiId: string): void => {
    trackEvent('dev-portal-api-remove-credentials', {apiId});
};

// API SDKs
export const trackAPISDKNavBarClick = (): void => {
    trackEvent('dev-portal-sidebar-api-sdks');
};

export const trackApiSDKDownload = (apiId: string, sdkZipName: string): void => {
    trackEvent('dev-portal-api-sdks-download', {
        apiId,
        sdkZipName
    });
};

// API Contracts
export const trackAPIContractsNavBarClick = (): void => {
    trackEvent('dev-portal-sidebar-api-contracts');
};

export const trackApiContractUpdate = (
    applicationId: string,
    apiId: string,
    throttlingPolicy: string,
    status: string,
    subscriptionId: string
): void => {
    trackEvent('dev-portal-api-contract-update', {
        applicationId,
        apiId,
        throttlingPolicy,
        status,
        subscriptionId
    });
};

// Application Events
export const trackApplicationClickOnHeader = (): void => {
    trackEvent('dev-portal-navbar-applications');
};

export const trackApplicationCreation = (
    appId: string,
    perTokenQuota: string,
    appDescription: string): void => {

    trackEvent('dev-portal-click-create-application', {
        appId,
        perTokenQuota,
        appDescription
    });
};

export const trackApplicationSelection = (appId: string): void => {
    trackEvent('dev-portal-click-select-application', {
        appId
    });
};

export const trackApplicationSelectionEdit = (appId: string): void => {
    trackEvent('dev-portal-click-select-application-edit', {
        appId
    });
};

export const trackApplicationSelectionDelete = (appId: string): void => {
    trackEvent('dev-portal-click-select-application-delete', {
        appId
    });
};

export const trackApplicationInfoBarEdit = (appId: string): void => {
    trackEvent('dev-portal-application-edit', {
        appId
    });
};

export const trackApplicationInfoBarDelete = (appId: string): void => {
    trackEvent('dev-portal-application-delete', {
        appId
    });
};

export const trackAppProdKeyGeneration = (
    applicationId: string,
    applicationTokenExpiryTime: string,
    refreshTokenExpiryTime: string,
    idTokenExpiryTime: string
): void => {
    trackEvent('dev-portal-application-prod-keys-generate', {
        appId: applicationId,
        applicationTokenExpiryTime,
        refreshTokenExpiryTime,
        idTokenExpiryTime
    });
};

export const trackAppTestKeyGeneration = (applicationId: string): void => {
    trackEvent('dev-portal-application-generate-test-token', {
        appId: applicationId
    });
};

export const trackAddingAppSubscription = (): void => {
    trackEvent('dev-portal-application-add-api');
};
